import { blockTimeRecordStatusLabels } from 'src/app/ReusableComponent/block-time-report/block-time-report.config';
import { IBlockTimeItem } from 'src/app/ReusableComponent/block-time-report/block-time-report.model';
import { BlockTimeLegEnum } from '../enums/block-time-leg.enum';
import { BlockTimeHoursCardsInterface } from '../interfaces/block-time-hours-cards.interface';
import { BlockTimeLegRowInterface } from '../interfaces/block-time-leg-row.interface';
import {
  BLOCK_TIME_ACTUAL_CARD_TITLE,
  BLOCK_TIME_ACTUAL_TOTAL_LABEL,
  BLOCK_TIME_EMPTY_VALUE,
  BLOCK_TIME_ESTIMATED_CARD_TITLE,
  BLOCK_TIME_ESTIMATED_TOTAL_LABEL,
  BLOCK_TIME_LEG_LABELS,
  BLOCK_TIME_NOT_RECORDED,
} from '../properties/block-time-record-modal.properties';
import { calculateBlockTimeVariance } from './calculate-block-time-variance.helper';
import { formatBlockHours } from './format-block-hours.helper';
import { isIbRepoHoursExcluded } from './is-ib-repo-hours-excluded.helper';

/**
 * Maps a block time record to the estimated card and the actual card.
 * The estimate is built on the planned itinerary, so the estimated legs show the planned airports and routing.
 */
export function mapBlockTimeHoursCards(record: Partial<IBlockTimeItem>): BlockTimeHoursCardsInterface {
  const landedIbAirport = record.actualInBoundReposition || null;
  const plannedIbAirport = record.inboundReposition || null;
  const isIbAirportMismatch = Boolean(landedIbAirport && plannedIbAirport && landedIbAirport !== plannedIbAirport);
  const status = record.blockTimeRecordStatus;

  return {
    estimated: {
      title: BLOCK_TIME_ESTIMATED_CARD_TITLE,
      legs: [
        buildLeg(BlockTimeLegEnum.ObRepo, record.outboundReposition, record.estimatedObHours),
        buildLeg(BlockTimeLegEnum.LiveFlight, record.routing, record.estimatedLiveHours),
        buildLeg(BlockTimeLegEnum.IbRepo, plannedIbAirport, record.estimatedIbHours),
      ],
      totalLabel: BLOCK_TIME_ESTIMATED_TOTAL_LABEL,
      totalHours: formatBlockHours(record.estimatedBlockHours),
      statusLabel: null,
      variance: null,
      isIbExcluded: false,
    },
    actual: {
      title: BLOCK_TIME_ACTUAL_CARD_TITLE,
      legs: [
        // Tracking does not capture the OB departure airport, so the assigned one is shown.
        buildLeg(BlockTimeLegEnum.ObRepo, record.outboundReposition, record.obEstimatedBlockHours, BLOCK_TIME_NOT_RECORDED),
        buildLeg(BlockTimeLegEnum.LiveFlight, record.routing, record.liveFlightHours),
        {
          ...buildLeg(BlockTimeLegEnum.IbRepo, landedIbAirport || plannedIbAirport, record.ibEstimatedBlockHours, BLOCK_TIME_NOT_RECORDED),
          plannedPlace: isIbAirportMismatch ? `Planned ${plannedIbAirport}` : null,
        },
      ],
      totalLabel: BLOCK_TIME_ACTUAL_TOTAL_LABEL,
      totalHours: formatBlockHours(record.actualBlockTime),
      statusLabel: status ? (blockTimeRecordStatusLabels[status] ?? status) : null,
      variance: calculateBlockTimeVariance(record.estimatedBlockHours, record.actualBlockTime),
      isIbExcluded: isIbRepoHoursExcluded({
        ibRepoAwaitingVerification: Boolean(record.ibRepoAwaitingVerification),
        ibRepoVerificationReason: record.ibRepoVerificationReason ?? null,
      }),
    },
  };
}

function buildLeg(
  leg: BlockTimeLegEnum,
  place: string | null | undefined,
  hours: string | number | null | undefined,
  emptyPlace: string = BLOCK_TIME_EMPTY_VALUE,
): BlockTimeLegRowInterface {
  return {
    leg,
    ...BLOCK_TIME_LEG_LABELS[leg],
    place: place?.trim() || emptyPlace,
    hours: formatBlockHours(hours),
    plannedPlace: null,
  };
}
